// FeeRateInput — operator input for a fee rate stored as int64 minor
// units scaled by fee_rate_precision.
//
// The operator types a human percentage (e.g. "7" for 7%); the field
// returns the minor-unit BIGINT string that goes on the wire. The
// peak-rate semantics matter:
//
//   Fees follow the Polymarket curved formula
//     fee = qty_human * rate * p * (1 - p)
//   where `rate` is the PEAK rate (at p=0.5). A rate of 7% therefore
//   charges $1.75 on a 100-ticket fill at $0.50 (SM-460 $0–$1 price
//   scale). The preview shows that number so the operator sees the
//   impact at a glance.
//
// Wire minor units are an implementation detail; they never render in
// the UI (operators read percentages, auditors read the API payloads).
import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/cn";
import { FeeExampleLines } from "@/components/fees/FeeExample";
import { exampleCurrency } from "@/components/fees/feeCurrency";
import type { CurrencyCode } from "@/currency";

export interface FeeRateInputProps {
  label: string;
  // The current value in MINOR units (int64 string). "0" or "" are
  // both treated as zero.
  value: string;
  // fee_rate_precision the value is scaled by. Almost always 6.
  precision: number;
  onChange: (minor: string) => void;
  disabled?: boolean;
  hint?: string;
  // Optional preview anchor — when set, the helper text shows what fee
  // would apply to a 100-ticket fill at half odds using this rate.
  // Default on; pass false to hide.
  showPreview?: boolean;
  // The currencies this rate will be collected in (SM-503) — one worked
  // example per currency, in that currency's configured unit. Defaults
  // to Gold, which is what every fill on the platform paid before the
  // Game book existed.
  currencies?: readonly CurrencyCode[];
}

// minorToPercent converts the int64 minor string at `precision` to a
// human percentage string. Precision=6 → "70000" → "7" (which means
// 7%). We keep trailing zeros off, but never strip the decimal point
// while the user is mid-type — this only runs on already-committed
// values.
export function minorToPercent(minor: string, precision: number): string {
  if (!minor || minor === "0") return "";
  // Pad so we can slice an explicit integer + fraction split. The
  // human percent is `rate * 100`, i.e. shift one fewer place than the
  // raw rate-as-fraction.
  const shift = Math.max(precision - 2, 0);
  const negative = minor.startsWith("-");
  const digits = (negative ? minor.slice(1) : minor).replace(/^0+/, "") || "0";
  const padded = digits.padStart(shift + 1, "0");
  const intPart = padded.slice(0, padded.length - shift) || "0";
  const fracPart = padded.slice(padded.length - shift).replace(/0+$/, "");
  const body = fracPart ? `${intPart}.${fracPart}` : intPart;
  return negative ? `-${body}` : body;
}

// percentToMinor parses a human percentage string ("7", "0.07", "7.5")
// at `precision` and returns the int64-as-string minor value. Empty /
// invalid → "0".
export function percentToMinor(input: string, precision: number): string {
  const trimmed = input.trim();
  if (!trimmed || trimmed === "-") return "0";
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return "0";
  const shift = Math.max(precision - 2, 0);
  const negative = trimmed.startsWith("-");
  const body = negative ? trimmed.slice(1) : trimmed;
  const [intPart, fracPartRaw = ""] = body.split(".");
  const fracPart = (fracPartRaw + "0".repeat(shift)).slice(0, shift);
  const combined = (intPart + fracPart).replace(/^0+(?=\d)/, "") || "0";
  return negative ? `-${combined}` : combined;
}

export function FeeRateInput({
  label,
  value,
  precision,
  onChange,
  disabled,
  hint,
  showPreview = true,
  currencies,
}: FeeRateInputProps) {
  // Local input state preserves what the user actually typed, even
  // when the canonicalised wire value is the same as before. Two
  // typing flows broke without this:
  //
  //   • "0"   → percentToMinor → "0" → minorToPercent → ""  (cleared)
  //   • "0."  → percentToMinor → "0" → minorToPercent → ""  (cleared
  //     mid-fractional input, so the user can never type 0.5)
  //
  // The wire value is the source of truth for what we POST; the local
  // string is the source of truth for what's on screen. We re-sync
  // from `value` only when the parent changes it to something that
  // doesn't round-trip back to the current local input — that keeps
  // external resets (selection change, mutation success) working
  // without clobbering an in-progress edit.
  const wireDisplay = useMemo(
    () => minorToPercent(value, precision),
    [value, precision],
  );
  const [local, setLocal] = useState<string>(() =>
    value === "0" ? "0" : wireDisplay,
  );
  useEffect(() => {
    const roundTrip = percentToMinor(local, precision);
    if (roundTrip !== value) {
      setLocal(value === "0" ? "0" : wireDisplay);
    }
    // We intentionally watch `value`/`precision`/`wireDisplay` only;
    // `local` is the editable buffer we mutate from this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, precision, wireDisplay]);

  // Preview: peak fee on a 100-ticket fill at half odds (p=0.5, so the
  // curve evaluates to qty * rate * 0.25), drawn in the currency this
  // policy prices — the arithmetic and the units both live in
  // <FeeExampleLines> (SM-503), because the units are a property of the
  // policy, not of this input.
  //
  // A caller that names none is editing a legacy policy, which prices
  // every book; the example falls back to Gold rather than showing
  // three lines for one number (SM-510).
  const exampleCurrencies = useMemo(
    () => (currencies?.length ? currencies : [exampleCurrency(undefined)]),
    [currencies],
  );

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-2xs font-semibold uppercase tracking-wide text-t-3">
        {label}
      </label>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={local}
          disabled={disabled}
          placeholder="0"
          onChange={(e) => {
            // Allow `digits`, `.`, `-`. Strip everything else but
            // keep partial fractions like "0." or "." so the user can
            // build "0.5" one keystroke at a time.
            const sanitized = e.target.value.replace(/[^\d.-]/g, "");
            setLocal(sanitized);
            onChange(percentToMinor(sanitized, precision));
          }}
          className={cn(
            "h-10 w-full rounded-md border border-line-1 bg-bg-2 pl-3 pr-10 text-sm text-t-1 placeholder:text-t-4 transition-colors focus:border-line-3 focus:outline-none focus:ring-4 focus:ring-neon/15",
            disabled && "cursor-not-allowed opacity-60",
          )}
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted">
          %
        </span>
      </div>
      <p className="text-2xs leading-relaxed text-t-3">
        {hint ??
          "The top rate, charged at 50/50 odds. The fee tapers to zero as odds approach 0% or 100%."}
      </p>
      {showPreview && (
        <FeeExampleLines
          rate={value}
          precision={precision}
          codes={exampleCurrencies}
        />
      )}
    </div>
  );
}
